/**
 * Header/AppBar Component
 */

import React from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  ListItemIcon,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  AutoStories as BookIcon,
  Psychology as BrainIcon,
  Search as SearchIcon,
  NotificationsOutlined as NotificationsSettingsIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { NOTIFICATION_SETTINGS_ROLES } from "@/pages/settings/notificationSettingsRows";
import { useUIStore } from "@/store/uiStore";
import { useLogout } from "@/hooks/useAuth";
import { useCurrentTenant } from "@/hooks/useTenants";
import { CampusSelector } from "./CampusSelector";
import { ContextualHelpButton } from "./ContextualHelpButton";
import { NotificationBadge } from "@/components/common";
import { useWebSocketNotifications } from "@/hooks/useWebSocket";

const DRAWER_WIDTH = 280;

interface HeaderProps {
  /**
   * Opens the search palette. The dialog itself belongs to AppShell: two
   * copies of it meant Ctrl+K opened two stacked dialogs with duplicate
   * element ids, and — once search started hitting the API — every search
   * ran twice.
   */
  onOpenSearch: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onOpenSearch }) => {
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const { user } = useAuthStore();
  const hasNotificationSettings = (
    NOTIFICATION_SETTINGS_ROLES as readonly string[]
  ).includes(user?.role ?? "");
  // Selector subscriptions: avoid re-rendering the whole header on unrelated
  // UI-store changes (e.g. campus/section toggles).
  const sidebarOpen = useUIStore((s) => s.sidebarOpen);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const mobileNavOpen = useUIStore((s) => s.mobileNavOpen);
  const toggleMobileNav = useUIStore((s) => s.toggleMobileNav);
  const theme = useUIStore((s) => s.theme);
  const setTheme = useUIStore((s) => s.setTheme);
  const logoutMutation = useLogout();

  // Super users don't have a tenant, so skip fetching tenant info for them
  const isSuperUser = user?.role === "super_user";
  const { data: tenant } = useCurrentTenant(isSuperUser);

  // Initialize WebSocket toast notifications
  useWebSocketNotifications();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const initials = `${user?.first_name?.[0] ?? ""}${user?.last_name?.[0] ?? ""}`;

  // Platform detection for shortcut display
  const isMac = typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

  // Ctrl/Cmd+K is handled once, by KeyboardShortcutsProvider in AppShell.
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleMenuClose();
    navigate("/profile");
  };

  const handleLogout = () => {
    handleMenuClose();
    logoutMutation.mutate(undefined, {
      onSettled: () => {
        navigate("/login");
      },
    });
  };

  const handleThemeToggle = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  // On mobile, sidebar overlays content so header should be full width
  const shouldShiftContent = sidebarOpen && !isMobile;

  return (
    <AppBar
      position="fixed"
      sx={{
        width: shouldShiftContent ? `calc(100% - ${DRAWER_WIDTH}px)` : "100%",
        ml: shouldShiftContent ? `${DRAWER_WIDTH}px` : 0,
        transition: (t) =>
          t.transitions.create(["margin", "width"], {
            easing: t.transitions.easing.sharp,
            duration: t.transitions.duration.leavingScreen,
          }),
        bgcolor: "background.paper",
        color: "text.primary",
        boxShadow: 1,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
      elevation={0}
    >
      <Toolbar>
        {/* Menu Toggle */}
        <IconButton
          edge="start"
          color="inherit"
          aria-label="menu"
          aria-expanded={isMobile ? mobileNavOpen : sidebarOpen}
          onClick={isMobile ? toggleMobileNav : toggleSidebar}
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>

        {/* Logo and Tenant Name */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {/* Logo Icon */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: { xs: 36, sm: 48 },
              height: { xs: 32, sm: 40 },
              borderRadius: "8px",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              px: 0.5,
            }}
          >
            <BookIcon
              aria-hidden="true"
              sx={{
                fontSize: { xs: 16, sm: 20 },
                color: "white",
              }}
            />
            <BrainIcon
              aria-hidden="true"
              sx={{
                fontSize: { xs: 16, sm: 20 },
                color: "white",
              }}
            />
          </Box>

          {/* App Name and Tenant */}
          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Ilm IQ
            </Typography>
            {tenant?.name && (
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontWeight: 500,
                  lineHeight: 1,
                  display: { xs: "none", md: "block" },
                }}
              >
                {tenant.name}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Search Button */}
        <Box
          role="button"
          tabIndex={0}
          // The visible shortcut is part of the name (WCAG 2.5.3 label in
          // name): a parenthetical "(Ctrl+K)" is dropped by the comparison.
          aria-label={`Search, ${isMac ? "⌘K" : "Ctrl+K"}`}
          aria-keyshortcuts={isMac ? "Meta+K" : "Control+K"}
          onClick={onOpenSearch}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onOpenSearch();
            }
          }}
          sx={{
            display: { xs: "none", sm: "flex" },
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 0.75,
            mr: 2,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
            cursor: "pointer",
            color: "text.secondary",
            "&:hover, &:focus-visible": {
              bgcolor: "action.hover",
              borderColor: "text.secondary",
            },
            "&:focus-visible": {
              outline: "2px solid",
              outlineColor: "primary.main",
              outlineOffset: 2,
            },
            transition: "all 0.15s ease",
            minWidth: 180,
          }}
        >
          <SearchIcon sx={{ fontSize: 20 }} />
          <Typography variant="body2" sx={{ flex: 1 }}>
            Search...
          </Typography>
          <Typography
            variant="caption"
            sx={{
              px: 0.75,
              py: 0.25,
              borderRadius: 0.5,
              bgcolor: "action.hover",
              fontFamily: "monospace",
              fontSize: 11,
              lineHeight: 1.2,
            }}
          >
            {isMac ? "⌘K" : "Ctrl+K"}
          </Typography>
        </Box>

        {/* Mobile Search Button */}
        <IconButton
          color="inherit"
          onClick={onOpenSearch}
          sx={{ display: { xs: "inline-flex", sm: "none" }, mr: 1 }}
          aria-label="Search"
        >
          <SearchIcon />
        </IconButton>

        {/* Campus Selector - not shown for platform admins (super_user) */}
        {!isSuperUser && (
          <Box sx={{ mr: 2 }}>
            <CampusSelector />
          </Box>
        )}

        {/* Contextual help — opens the most relevant guide for this page. */}
        <ContextualHelpButton />

        {/* Theme Toggle */}
        <IconButton onClick={handleThemeToggle} color="inherit" sx={{ mr: 1 }} aria-label={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}>
          {theme === "light" ? <DarkModeIcon /> : <LightModeIcon />}
        </IconButton>

        {/* Notifications */}
        <NotificationBadge />

        {/* User Menu */}
        <IconButton
          onClick={handleMenuOpen}
          size="small"
          // The avatar's initials are the button's visible text, so the name
          // must contain them (WCAG 2.5.3 label in name).
          aria-label={initials ? `Account menu, ${initials}` : "Account menu"}
          aria-controls={menuOpen ? "account-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={menuOpen ? "true" : undefined}
        >
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: "primary.main",
            }}
          >
            {initials}
          </Avatar>
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          id="account-menu"
          open={menuOpen}
          onClose={handleMenuClose}
          onClick={handleMenuClose}
          slotProps={{
            paper: {
              elevation: 3,
              sx: {
                minWidth: 220,
                mt: 1.5,
                "& .MuiMenuItem-root": {
                  px: 2,
                  py: 1.5,
                },
              },
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          {/* User Info */}
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2" sx={{
              fontWeight: 600
            }}>
              {user?.first_name} {user?.last_name}
            </Typography>
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              {user?.email}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: "inline-block",
                mt: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: 1,
                bgcolor: "primary.light",
                color: "primary.main",
                fontWeight: 600,
              }}
            >
              {user?.role.replace("_", " ").toUpperCase()}
            </Typography>
          </Box>

          <Divider />

          <MenuItem onClick={handleProfile}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            Profile
          </MenuItem>

          {hasNotificationSettings && (
            <MenuItem
              onClick={() => {
                handleMenuClose();
                navigate("/settings/notifications");
              }}
            >
              <ListItemIcon>
                <NotificationsSettingsIcon fontSize="small" />
              </ListItemIcon>
              Notification settings
            </MenuItem>
          )}

          <Divider />

          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </Toolbar>
      {/* Global Search Dialog */}
    </AppBar>
  );
};
